import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Paper,
  CircularProgress,
  TextField,
  IconButton,
  Alert,
  InputAdornment,
  Snackbar,
  Autocomplete,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useAuth } from '../../../contexts/AuthContext';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface Cashbox {
  id: string;
  name: string;
  description?: string;
  currency: string;
  balance: number;
  createdAt?: string;
  isActive?: boolean;
  branchId?: string | null;
  isDefault?: boolean;
  hasTransactions?: boolean;
}

interface CashboxTransferModalProps {
  open: boolean;
  onClose: () => void;
  sourceCashbox: Cashbox;
  companyId: string | null;
  onSuccess?: () => void;
}

const CashboxTransferModal: React.FC<CashboxTransferModalProps> = ({
  open,
  onClose,
  sourceCashbox,
  companyId,
  onSuccess
}) => {
  const { userDetails } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [insufficientFunds, setInsufficientFunds] = useState(false);

  // Form states
  const [allCashboxes, setAllCashboxes] = useState<Cashbox[]>([]);
  const [targetCashboxes, setTargetCashboxes] = useState<Cashbox[]>([]);
  const [selectedTargetCashbox, setSelectedTargetCashbox] = useState<Cashbox | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState('');
  const [documentNo, setDocumentNo] = useState<string>('');
  // ✅ Geçmiş tarihli işlem: toggle açıldığında tarih seçici görünür.
  // İşaretli ve tarih girilmişse hareket kaydı TAMAMEN o tarihe yazılır (Date + CreatedAt).
  const [isBackdated, setIsBackdated] = useState(false);
  const [transactionDate, setTransactionDate] = useState<string>(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  });

  // Otomatik belge numarası oluştur.
  // ✅ Geçmiş tarihli işlemde tarih kısmı (YYYYMMDD) seçilen tarihe göre üretilir;
  //    böylece belge numarası da bugüne dair iz taşımaz.
  const generateDocumentNo = (baseDate?: Date) => {
    const now = new Date();
    const datePart = baseDate ?? now;
    const year = datePart.getFullYear();
    const month = String(datePart.getMonth() + 1).padStart(2, '0');
    const day = String(datePart.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    
    // Format: KT-YYYYMMDD-HHMMSS (Kasa Transferi)
    return `KT-${year}${month}${day}-${hours}${minutes}${seconds}`;
  };

  // İşlem tarihi string'inden (YYYY-MM-DD) Date üret (öğlen sabitli)
  const parseTransactionDate = (value: string): Date | null => {
    const parts = value.split('-');
    if (parts.length !== 3) return null;
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 12, 0, 0);
    return isNaN(d.getTime()) ? null : d;
  };

  // Geçmiş tarihli işlem toggle'ı
  const handleBackdatedToggle = (checked: boolean) => {
    setIsBackdated(checked);
    if (checked) {
      const d = parseTransactionDate(transactionDate);
      setDocumentNo(generateDocumentNo(d ?? undefined));
    } else {
      setDocumentNo(generateDocumentNo());
    }
  };

  // Tarih seçici değişimi: belge numarasını da seçilen tarihe göre yenile
  const handleTransactionDateChange = (value: string) => {
    setTransactionDate(value);
    const d = parseTransactionDate(value);
    if (d) {
      setDocumentNo(generateDocumentNo(d));
    }
  };

  useEffect(() => {
    if (open) {
      setError(null);
      setSuccess(false);
      setSuccessMessage('');
      setSelectedTargetCashbox(null);
      setAmount('');
      setDescription('');
      setDocumentNo(generateDocumentNo());
      // ✅ Geçmiş tarihli işlem alanını sıfırla (varsayılan: kapalı / bugün)
      setIsBackdated(false);
      const today = new Date();
      setTransactionDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
      loadCashboxes();
    }
  }, [open, companyId]);

  // Kaynak kasa hariç diğer kasaları filtrele
  useEffect(() => {
    if (allCashboxes.length > 0) {
      const filtered = allCashboxes.filter(
        cashbox => 
          cashbox.id !== sourceCashbox.id && // Kaynak kasayı hariç tut
          cashbox.currency === sourceCashbox.currency && // Aynı para birimindeki kasalar
          cashbox.isActive !== false // Aktif kasalar
      );
      setTargetCashboxes(filtered);
    }
  }, [allCashboxes, sourceCashbox]);

  const getCurrencySymbol = (code: string): string => {
    switch(code) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'JPY': return '¥';
      case 'CHF': return 'Fr.';
      case 'AUD': return 'A$';
      case 'CAD': return 'C$';
      case 'CNY': return '¥';
      default: return code;
    }
  };

  const loadCashboxes = async () => {
    try {
      setLoading(true);
      
      const response = await accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY);
      const sqlCashboxes = response.data.data || response.data;
      
      if (!sqlCashboxes || sqlCashboxes.length === 0) {
        setAllCashboxes([]);
        setLoading(false);
        return;
      }
      
      // Türk formatından sayıya çevirme
      const parseTurkishNumber = (value: any): number => {
        if (typeof value === 'number') return value;
        if (!value) return 0;
        const strValue = String(value);
        const cleanValue = strValue.replace(/\./g, '').replace(',', '.');
        return parseFloat(cleanValue) || 0;
      };
      
      const cashboxList: Cashbox[] = sqlCashboxes
        .filter((data: any) => data.isActive !== false)
        .map((data: any) => ({
          id: `${data.id}`,
          name: data.name || '',
          description: data.description || '',
          currency: data.currency || 'TRY',
          balance: parseTurkishNumber(data.balance),
          createdAt: data.createdAt || '',
          isActive: data.isActive !== false,
          branchId: data.branchId || null,
          isDefault: data.isDefault || false,
          hasTransactions: data.hasTransactions || false
        }));
      
      setAllCashboxes(cashboxList);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      setError(tt('Kasalar yüklenirken hata oluştu: ', 'An error occurred while loading cashboxes: ') + err.message);
    }
  };

  const formatCurrency = (amount: number, currencyCode: string = 'TRY') => {
    return new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Sayıyı Türk formatına çevir (1000 → "1.000") - Ondalık destekli
  const formatNumberWithThousandsSeparator = (value: string): string => {
    // Sadece rakamları ve virgülü tut
    const cleanValue = value.replace(/[^\d,]/g, '');
    
    // Birden fazla virgül varsa sadece ilkini tut
    const commaCount = (cleanValue.match(/,/g) || []).length;
    if (commaCount > 1) {
      const firstCommaIndex = cleanValue.indexOf(',');
      const beforeComma = cleanValue.substring(0, firstCommaIndex);
      const afterComma = cleanValue.substring(firstCommaIndex + 1).replace(/,/g, '');
      return formatNumberWithThousandsSeparator(beforeComma + ',' + afterComma);
    }
    
    // Virgülden önceki ve sonraki kısımları ayır
    const parts = cleanValue.split(',');
    const integerPart = parts[0];
    let decimalPart = parts.length > 1 ? parts[1] : null; // null = virgül yok, '' = virgül var ama rakam yok
    
    // Ondalık kısmı maksimum 2 basamak ile sınırla
    if (decimalPart && decimalPart.length > 2) {
      decimalPart = decimalPart.substring(0, 2);
    }
    
    // Binlik ayraçlarını ekle (integer kısmına)
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Decimal varsa ekle (virgül girilmişse, rakam girilmese bile virgülü göster)
    return decimalPart !== null ? `${formattedInteger},${decimalPart}` : formattedInteger;
  };

  // Formatlanmış sayıdan ham sayı al ("1.000,50" → 1000.50)
  const parseFormattedNumber = (value: string): number => {
    const cleanValue = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  // Miktar değişikliği
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatNumberWithThousandsSeparator(value);
    setAmount(formatted);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      setInsufficientFunds(false);
      
      if (!companyId || !selectedTargetCashbox) {
        setError(tt('Lütfen hedef kasa seçiniz.', 'Please select a target cashbox.'));
        setLoading(false);
        return;
      }
      
      const amountValue = parseFormattedNumber(amount);
      if (isNaN(amountValue) || amountValue <= 0) {
        setError(tt('Geçerli bir miktar giriniz.', 'Please enter a valid amount.'));
        setLoading(false);
        return;
      }
      
      // ✅ YENİ: Frontend bakiye kontrolü KALDIRILDI!
      // Bakiyeler artık işlem kayıtlarından hesaplandığı için,
      // frontend'deki sourceCashbox.balance güncel olmayabilir.
      // Backend zaten doğru bakiye kontrolünü yapacak.
      
      // Belge numarası kontrolü
      if (!documentNo || documentNo.trim() === '') {
        setError(tt('Belge numarası zorunludur. Lütfen belge numarası giriniz.', 'Document number is required. Please enter a document number.'));
        setLoading(false);
        return;
      }

      // ✅ Geçmiş tarihli işlem: işaretliyse seçilen tarihi hesapla.
      // Hareket kaydı TAMAMEN bu tarihe yazılacak (backend Date + CreatedAt'i bu değere ayarlar).
      let backdatedIso: string | null = null;
      if (isBackdated) {
        const parts = transactionDate.split('-');
        if (parts.length !== 3) {
          setError(tt('Geçerli bir işlem tarihi seçiniz.', 'Please select a valid transaction date.'));
          setLoading(false);
          return;
        }
        const selected = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 12, 0, 0);
        if (isNaN(selected.getTime())) {
          setError(tt('Geçerli bir işlem tarihi seçiniz.', 'Please select a valid transaction date.'));
          setLoading(false);
          return;
        }
        // Gün kayması yaşanmaması için öğlen (12:00) sabitlenir.
        backdatedIso = selected.toISOString();
      }
      
      const transferPayload: Record<string, any> = {
        sourceCashboxId: parseInt(sourceCashbox.id),
        targetCashboxId: parseInt(selectedTargetCashbox.id),
        amount: amountValue,
        description: description || tt(`${sourceCashbox.name} → ${selectedTargetCashbox.name} kasa transferi`, `${sourceCashbox.name} → ${selectedTargetCashbox.name} cash transfer`),
        documentNo: documentNo.trim(),
        createdBy: userDetails?.email || 'Sistem',
      };
      // Yalnızca geçmiş tarihli işlem seçildiyse tarihi gönder; aksi halde backend bugünü kullanır.
      if (backdatedIso) {
        transferPayload.transactionDate = backdatedIso;
      }
      
      console.log('💸 [TRANSFER] İstek gönderiliyor:', transferPayload);

      const response = await accountingApi.post(ACCOUNTING_API.CASHBOX_TRANSFER, transferPayload);
      const result = response.data;
      console.log('✅ [TRANSFER] Transfer başarılı:', result);
      
      setSuccess(true);
      setSuccessMessage(tt('Kasa transferi başarıyla tamamlandı!', 'Cash transfer completed successfully!'));
      
      // 1.5 saniye sonra modalı kapat
      setTimeout(() => {
        setSuccess(false);
        if (onSuccess) onSuccess();
        onClose();
      }, 1500);
      
    } catch (error: any) {
      console.error('❌ [TRANSFER] Kasa transferi hatası:', error);
      setError(error.response?.data?.message || (error instanceof Error ? error.message : tt('Kasa transferi sırasında bir hata oluştu', 'An error occurred during cash transfer')));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <SwapHorizIcon color="info" />
              <Typography variant="h6">{tt('Kasa Transferi', 'Cash Transfer')}</Typography>
            </Box>
            <IconButton edge="end" color="inherit" onClick={onClose} disabled={loading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {success ? (
            <Box textAlign="center" p={3}>
              <CheckCircleOutlineIcon color="success" style={{ fontSize: 60 }} />
              <Typography variant="h6" color="success" gutterBottom sx={{ mt: 2 }}>
                {successMessage}
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Kaynak Kasa Bilgisi */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'primary.50', borderColor: 'primary.main' }}>
                <Typography variant="caption" color="textSecondary" gutterBottom>
                  {tt('Kaynak Kasa', 'Source Cashbox')}
                </Typography>
                <Typography variant="h6" gutterBottom>{sourceCashbox.name}</Typography>
                <Typography 
                  variant="body1" 
                  color={sourceCashbox.balance >= 0 ? "success.main" : "error.main"} 
                  fontWeight="bold"
                >
                  {tt('Bakiye', 'Balance')}: {formatCurrency(sourceCashbox.balance, sourceCashbox.currency)}
                </Typography>
              </Paper>

              {error && (
                <Alert severity={insufficientFunds ? "warning" : "error"} sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Hedef Kasa Seçimi */}
              <Autocomplete
                options={targetCashboxes}
                getOptionLabel={(option) => 
                  `${option.name} (${tt('Bakiye', 'Balance')}: ${option.balance.toLocaleString(localeCode, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} ${getCurrencySymbol(option.currency)})`
                }
                value={selectedTargetCashbox}
                onChange={(event, newValue) => {
                  setSelectedTargetCashbox(newValue);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={loading || targetCashboxes.length === 0}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tt('Hedef Kasa *', 'Target Cashbox *')}
                    placeholder={tt('Kasa ara...', 'Search cashbox...')}
                    required
                  />
                )}
                noOptionsText={tt('Transfer edilebilecek kasa bulunamadı', 'No cashbox available for transfer')}
                sx={{ mb: 2 }}
              />
              
              {targetCashboxes.length === 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {tt('ℹ️ Transfer edilebilecek kasa bulunamadı. Hedef kasa, kaynak kasa ile aynı para birimine sahip ve aktif olmalıdır.', 'No cashbox available for transfer. Target cashbox must be active and have the same currency as source cashbox.')}
                </Alert>
              )}

              {/* Belge Numarası */}
              <TextField
                label={tt('Belge Numarası *', 'Document Number *')}
                value={documentNo}
                onChange={(e) => setDocumentNo(e.target.value)}
                fullWidth
                placeholder={tt('Örn: KT-2024-001', 'Ex: KT-2024-001')}
                sx={{ mb: 2 }}
                required
                disabled={loading}
                helperText={tt('Transfer belgesi için belge numarası giriniz', 'Enter a document number for transfer receipt')}
              />

              {/* Miktar */}
              <TextField
                label={tt('Miktar *', 'Amount *')}
                value={amount}
                onChange={handleAmountChange}
                fullWidth
                type="text"
                inputProps={{
                  style: { textAlign: 'right', fontSize: '1.2rem', fontWeight: 'bold' }
                }}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {getCurrencySymbol(sourceCashbox.currency)}
                    </InputAdornment>
                  ),
                }}
                required
                disabled={loading}
                helperText={`${tt('Maksimum transfer edilebilir', 'Maximum transferable')}: ${formatCurrency(sourceCashbox.balance, sourceCashbox.currency)}`}
                placeholder={tt('Örn: 1.000,50', 'Ex: 1,000.50')}
              />

              {/* Açıklama */}
              <TextField
                label={tt('Açıklama (Opsiyonel)', 'Description (Optional)')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder={tt('Transfer ile ilgili açıklama...', 'Description about the transfer...')}
                disabled={loading}
                sx={{ mb: 2 }}
              />

              {/* Geçmiş Tarihli İşlem */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isBackdated}
                    onChange={(e) => handleBackdatedToggle(e.target.checked)}
                    disabled={loading}
                    color="info"
                  />
                }
                label={tt('Geçmiş tarihli işlem yap', 'Record as a back-dated transaction')}
              />

              {isBackdated && (
                <>
                  <TextField
                    label={tt('İşlem Tarihi *', 'Transaction Date *')}
                    type="date"
                    value={transactionDate}
                    onChange={(e) => handleTransactionDateChange(e.target.value)}
                    fullWidth
                    required
                    disabled={loading}
                    sx={{ mt: 1, mb: 1 }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`
                    }}
                  />
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    {tt(
                      'Bu transfer seçtiğiniz tarihe kaydedilecektir. İşlem kaydı tamamen o tarihe ait olacak; bugüne dair hiçbir bilgi içermeyecektir.',
                      'This transfer will be recorded on the selected date. The transaction record will fully belong to that date and contain no reference to today.'
                    )}
                  </Alert>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        
        {!success && (
          <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
            <Button
              onClick={onClose}
              disabled={loading}
            >
              {tt('İptal', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              color="info"
              onClick={handleSubmit}
              disabled={loading || !selectedTargetCashbox || !amount || !documentNo || (isBackdated && !transactionDate)}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SwapHorizIcon />}
            >
              {loading ? tt('Transfer Yapılıyor...', 'Processing transfer...') : tt('💸 Transferi Tamamla', '💸 Complete Transfer')}
            </Button>
          </DialogActions>
        )}
      </Dialog>
      
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={successMessage}
      />
    </>
  );
};

export default CashboxTransferModal;

